import { BottomAction } from '@euler/app/components/BottomAction';
import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useQuotationFormContext } from '@euler/app/flows/task/pages/quotation/components/quotation-form/Context';
import { keyOfIssue } from '@euler/app/flows/task/pages/quotation/functions/utils';
import { QuotationIssueInfo } from '@euler/app/flows/task/pages/quotation/types/QuotationCatalog';
import { Center } from '@euler/components/Center';
import { Checkbox } from '@euler/components/form/Checkbox';
import {
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
  getDefectiveLevelFrom,
  getDefectiveLevelText,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useGeneralBottomSheetHeight,
  useGeneralBottomSheetModal,
} from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { MediaObject } from '@euler/model';
import { AbnormalLevel } from '@euler/model/enum';
import {
  CustomIssue,
  QuotationIssueMedia,
  QuotationIssuePayload,
  SiteInspection,
} from '@euler/model/task-detail';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { makeUniqueIdAlphabetic } from '@euler/utils/id';
import {
  BottomSheetFlatList,
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
} from '@gorhom/bottom-sheet';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from 'styled-components';
import invariant from 'tiny-invariant';

type Ref = {
  getSelectedIssues: () => QuotationIssueInfo[];
};

type Props = {
  selectedIssues?: QuotationIssuePayload[];
};

type TaskData = {
  preInspectionSites: SiteInspection[];
  preInspectionCustomIssues: CustomIssue[] | undefined;
  inspectionSites: SiteInspection[];
  inspectionCustomIssues: CustomIssue[] | undefined;
};

export const QuotationSubjectRelatedIssuesBottomSheet = memo(() => {
  useToggleAutomaticKeyboard(true);

  const {
    taskManager: { quotationManager },
  } = useTaskContext();
  const { subjectWhoseIssuesBeingEdited$ } = useQuotationFormContext();
  const [subjectWhoseIssuesBeingEdited] = useBehaviorSubject(
    subjectWhoseIssuesBeingEdited$,
  );

  const { rpx } = useResponsiveContext();
  const { navBarHeight } = useSystemMetrics();

  const ref = useRef<Ref>(null);

  const { bottomSheetModalRef, ...bottomSheetProps } =
    useGeneralBottomSheetModal({
      show: subjectWhoseIssuesBeingEdited != null,
      topMargin: navBarHeight,
      minSnapPoint: '50%',
      mediumSnapPoint: '75%',
      index: 1,
      enableDismissOnClose: true,
      enablePanDownToClose: true,
      dismissWhenPressBackdrop: true,
      onDismiss: () => {
        subjectWhoseIssuesBeingEdited$.next(undefined);
      },
    });
  const animatedPosition = useSharedValue(0);
  const height = useGeneralBottomSheetHeight();
  const style = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        animatedPosition.value,
        [(height * 2) / 4, height],
        [1, 0],
      ),
    };
  });

  const onCancel = usePersistFn(() => {
    bottomSheetModalRef.current?.dismiss();
  });

  const onDone = usePersistFn(async () => {
    if (ref.current == null) return;

    invariant(subjectWhoseIssuesBeingEdited != null);

    const issues = ref.current.getSelectedIssues();

    // create or update the material
    quotationManager.produce(draft => {
      const subject = draft.subjects.find(
        x => x.name === subjectWhoseIssuesBeingEdited.name,
      );

      invariant(subject != null);

      subject.issues = issues;
    });

    bottomSheetModalRef.current?.dismiss();
  });

  const Footer = usePersistFn((props: BottomSheetFooterProps) => {
    return (
      <BottomSheetFooter {...props}>
        <BottomAction containerStyle={style}>
          <BottomAction.Button
            text="取消"
            variant="default"
            onPress={onCancel}
          />
          <BottomAction.Button
            text="确定"
            variant="primary"
            css={`
              margin-left: ${rpx(15)}px;
            `}
            onPress={onDone}
          />
        </BottomAction>
      </BottomSheetFooter>
    );
  });

  return (
    <>
      <BottomSheetModal
        {...bottomSheetProps}
        footerComponent={Footer}
        animatedPosition={animatedPosition}
        keyboardBehavior="interactive"
        keyboardBlurBehavior="none"
        ref={bottomSheetModalRef}
      >
        <Content
          ref={ref}
          key={subjectWhoseIssuesBeingEdited?.name}
          selectedIssues={subjectWhoseIssuesBeingEdited?.issues}
        />
      </BottomSheetModal>
    </>
  );
});

type IssueGroup = {
  name: string;
  issues: QuotationIssueInfo[];
};

const Content = memo(
  forwardRef<Ref, Props>((props, ref) => {
    const { bottom } = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const { taskService } = useServiceFactory();
    const { detail, asyncChildTasks } = useTaskContext();

    const childTasks =
      asyncChildTasks.status === 'success' ? asyncChildTasks.result : undefined;

    const [data, setData] = useState<TaskData>(() => ({
      preInspectionSites: detail.preInspection.sites,
      preInspectionCustomIssues: detail.preInspection.customIssues,
      inspectionSites: detail.inspection.sites,
      inspectionCustomIssues: detail.inspection.customIssues,
    }));

    useEffect(() => {
      if (childTasks?.length) {
        Promise.all(
          childTasks.map(subTask =>
            taskService.getTaskDetail(subTask.taskNo, {
              projection: {
                preInspection: true,
                inspection: true,
                construction: true,
              },
            }),
          ),
        )
          .then(details => {
            setData({
              preInspectionSites: details.flatMap(
                x => x.response.preInspection.sites,
              ),
              preInspectionCustomIssues: details.flatMap(
                x => x.response.preInspection.customIssues ?? [],
              ),
              inspectionSites: details.flatMap(
                x => x.response.inspection.sites,
              ),
              inspectionCustomIssues: details.flatMap(
                x => x.response.inspection.customIssues ?? [],
              ),
            });
          })
          .catch(err => {
            console.error(err);
          });
      }
    }, [childTasks, taskService]);

    const groups = useMemo<IssueGroup[]>(() => {
      const groupMap = new Map<string, IssueGroup>();

      const addIssues = (name: string, issues: QuotationIssueInfo[]) => {
        let group = groupMap.get(name);
        if (group == null) {
          group = { name, issues: [] };
          groupMap.set(name, group);
        }
        for (const issue of issues) {
          issue.key = keyOfIssue(issue);
          if (!group.issues.some(x => x.key === issue.key)) {
            group.issues.push(issue);
          }
        }
      };

      const addFromInspectionResults = (
        siteInspections: SiteInspection[],
        customIssues: CustomIssue[],
      ) => {
        customIssues = [
          ...customIssues,
          ...siteInspections.flatMap(x => x.customIssues),
        ];
        for (const siteInspection of siteInspections) {
          for (const itemInspection of siteInspection.items) {
            if (
              !itemInspection.maintenanceAdvice ||
              itemInspection.abnormalLevel === AbnormalLevel.Fine
            ) {
              continue;
            }
            const issue: QuotationIssueInfo = {
              id: makeUniqueIdAlphabetic(),
              key: '',
              inspectionType: siteInspection.inspectionType,
              siteId: siteInspection.siteId,
              siteName: siteInspection.name ?? '',
              itemId: itemInspection.itemId,
              itemName: itemInspection.name,
              isCustomIssue: false,
              abnormalLevel: itemInspection.abnormalLevel,
              severityLevel: itemInspection.severityLevel,
              authorId: siteInspection.technicianId,
              authorName: siteInspection.technicianName,
              maintenanceAdvice: itemInspection.maintenanceAdvice,
              medias: itemInspection.medias.map<QuotationIssueMedia>(media => ({
                id: media.id,
                cid: media.cid,
                type: media.type,
                url: media.url,
                coverUrl: media.coverUrl,
                remark: media.remark,
              })),
              title: itemInspection.label || itemInspection.optionLabel || '',
              remark: itemInspection.remark,
            };
            addIssues(itemInspection.maintenanceAdvice, [issue]);
          }
        }

        for (const customIssue of customIssues) {
          if (
            !customIssue.maintenanceAdvice ||
            customIssue.abnormalLevel === AbnormalLevel.Fine
          ) {
            continue;
          }

          const issue: QuotationIssueInfo = {
            id: makeUniqueIdAlphabetic(),
            key: '',
            inspectionType: customIssue.inspectionType,
            siteId: customIssue.siteId,
            siteName: customIssue.siteName ?? '',
            itemId: customIssue.itemId,
            itemName: customIssue.itemName,
            isCustomIssue: true,
            abnormalLevel: customIssue.abnormalLevel,
            severityLevel: customIssue.severityLevel,
            authorId: customIssue.technicianId,
            authorName: customIssue.technicianName,
            maintenanceAdvice: customIssue.maintenanceAdvice,
            medias: customIssue.medias.map<QuotationIssueMedia>(media => ({
              id: media.id,
              cid: media.cid,
              type: media.type,
              url: media.url,
              coverUrl: media.coverUrl,
              remark: media.remark,
            })),
            title: [
              customIssue.siteName,
              customIssue.label?.replace('自定义: ', ''),
            ].join(': '),
            remark: customIssue.description,
            defectiveClass: customIssue.defectiveClass,
            inspectionResult: customIssue.inspectionResult,
          };

          addIssues(customIssue.maintenanceAdvice, [issue]);
        }
      };

      addFromInspectionResults(
        data.preInspectionSites,
        data.preInspectionCustomIssues ?? [],
      );

      addFromInspectionResults(
        data.inspectionSites,
        data.inspectionCustomIssues ?? [],
      );

      return [...groupMap.values()];
    }, [
      data.inspectionCustomIssues,
      data.inspectionSites,
      data.preInspectionCustomIssues,
      data.preInspectionSites,
    ]);

    const [selection, setSelection] = useState<Record<string, string[]>>(() => {
      const selected: Record<string, string[]> = {};
      if (props.selectedIssues) {
        for (const issue of props.selectedIssues) {
          if (!selected[issue.maintenanceAdvice]) {
            selected[issue.maintenanceAdvice] = [];
          }
          selected[issue.maintenanceAdvice].push(issue.key);
        }
      }
      return selected;
    });

    const getSelectedIssues = useCallback(() => {
      const selected: QuotationIssueInfo[] = [];
      for (const group of groups) {
        if (selection[group.name]) {
          selected.push(
            ...group.issues.filter(x => selection[group.name].includes(x.key)),
          );
        }
      }
      return selected;
    }, [groups, selection]);

    useImperativeHandle(ref, () => ({
      getSelectedIssues,
    }));

    const FlatListComponent =
      Platform.OS === 'web' ? FlatList : BottomSheetFlatList;

    const onSelectionChange = useCallback(
      (name: string, selected: string[]) => {
        setSelection(x => ({ ...x, [name]: selected }));
      },
      [],
    );

    const renderItem: ListRenderItem<IssueGroup> = useCallback(
      ({ item }) => {
        return (
          <ListItem
            group={item}
            selection={selection[item.name] ?? []}
            onSelectionChange={onSelectionChange}
          />
        );
      },
      [onSelectionChange, selection],
    );

    return (
      <FlatListComponent
        ListHeaderComponent={ListHeader}
        data={groups}
        keyExtractor={x => x.name}
        renderItem={renderItem}
        contentContainerStyle={{
          padding: rpx(15),
          paddingBottom: bottom + rpx(75),
        }}
      />
    );
  }),
);

const ListItem = memo(
  ({
    group,
    selection,
    style,
    onSelectionChange,
  }: {
    group: IssueGroup;
    selection: string[];
    style?: StyleProp<ViewStyle>;
    onSelectionChange?: (name: string, selection: string[]) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const medias = useMemo(
      () =>
        group.issues
          .flatMap(x => x.medias)
          .map<MediaObject>(x => ({
            id: x.cid,
            type: x.type.includes('image') ? 'image' : 'video',
            url: x.url,
            coverUrl: x.coverUrl,
          })),
      [group.issues],
    );

    const onIssueCheckChange = useCallback(
      (issue: QuotationIssueInfo, checked: boolean) => {
        if (checked) {
          onSelectionChange?.(group.name, [...selection, issue.key]);
        } else {
          onSelectionChange?.(
            group.name,
            selection.filter(x => x !== issue.key),
          );
        }
      },
      [group.name, onSelectionChange, selection],
    );

    const onGroupCheckChange = useCallback(
      (checked: boolean) => {
        if (checked) {
          onSelectionChange?.(
            group.name,
            group.issues.map(x => x.key),
          );
        } else {
          onSelectionChange?.(group.name, []);
        }
      },
      [group.issues, group.name, onSelectionChange],
    );

    return (
      <View
        style={style}
        css={`
          margin-bottom: ${rpx(15)}px;
        `}
      >
        <StackLayout
          direction="row"
          alignItems="center"
          gap={rpx(5)}
          paddingVertical={rpx(5)}
          mb={rpx(5)}
          borderBottomColor={'#ddd'}
          borderBottomWidth={StyleSheet.hairlineWidth}
        >
          <Checkbox
            size={rpx(16)}
            checked={group.issues.every(x => selection.includes(x.key))}
            onChange={onGroupCheckChange}
          />
          <Typography.Title level={3}>{group.name}</Typography.Title>
        </StackLayout>
        <StackLayout>
          {group.issues.map(issue => (
            <IssueItem
              key={issue.key}
              issue={issue}
              selected={selection.includes(issue.key)}
              onSelectChange={onIssueCheckChange}
            />
          ))}
        </StackLayout>
        {medias.length > 0 && (
          <MediaCarousel
            medias={medias}
            display="list"
            size={rpx(64)}
            itemSpacer={rpx(5)}
            playIconSize={rpx(21)}
            itemStyle={{ borderRadius: rpx(5), overflow: 'hidden' }}
            css={`
              margin-top: ${rpx(5)}px;
            `}
          />
        )}
      </View>
    );
  },
);

const IssueItem = memo(
  ({
    issue,
    selected,
    onSelectChange,
    style,
  }: {
    issue: QuotationIssueInfo;
    selected: boolean;
    onSelectChange?: (issue: QuotationIssueInfo, select: boolean) => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const title = issue.isCustomIssue ? issue.siteName : issue.itemName;
    const description = issue.isCustomIssue
      ? issue.inspectionResult
      : issue.title;

    const defectiveLevel = getDefectiveLevelFrom(issue);

    const onCheckChange = useCallback(
      (checked: boolean) => {
        onSelectChange?.(issue, checked);
      },
      [issue, onSelectChange],
    );

    return (
      <StackLayout
        style={style}
        direction="row"
        alignItems="center"
        gap={rpx(5)}
      >
        <Checkbox size={rpx(16)} checked={selected} onChange={onCheckChange} />
        <StackLayout direction="row" flex={1} alignItems="center">
          <Typography.Text size={rpx(14)}>{`${title}: `}</Typography.Text>
          <Typography.Text size={rpx(14)} color="#777">
            {description ?? ''}
          </Typography.Text>
        </StackLayout>
        <Center
          css={`
            padding: 0 ${rpx(7)}px;
            border-radius: ${rpx(44)}px;
            background-color: ${getDefectiveLevelBgColor(
              theme,
              defectiveLevel,
            )};
          `}
        >
          <Typography.Text
            size={rpx(12)}
            color={getDefectiveLevelColor(theme, defectiveLevel)}
          >
            {getDefectiveLevelText(defectiveLevel)}
          </Typography.Text>
        </Center>
      </StackLayout>
    );
  },
);

const ListHeader = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <Center
      css={`
        padding: ${rpx(10)}px 0;
        height: ${rpx(44)}px;
      `}
    >
      <Typography.Label size={rpx(16)} medium color="#333">
        关联报价项目相关故障
      </Typography.Label>
    </Center>
  );
});
